/**
 * FireworkArtwork
 * Author: Wataru Fukushima
 * License: CC BY-ND 4.0
 */

import Vue from 'vue'
import * as THREE from 'three'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass'
import { AfterimagePass } from 'three/examples/jsm/postprocessing/AfterimagePass'
import { UnrealBloomPass } from './UnrealBloomPass'
import { Firework } from './index'

export default class FireworkArtwork {
  _frame = 0
  WIDTH = window.innerWidth
  HEIGHT = window.innerHeight
  fireworks = []
  animating = true
  autoShoot = false
  autoShootTime = 0
  interval = 1
  stack = 1
  delay = 0
  delaySpendTime = 0
  canvas = null
  renderer = null
  camera = null
  composer = null
  scene = null
  eventBus = null

  constructor({ canvas, autoShoot = false, interval = 0.723 * 6, stack = 1, delay = 0 }) {
    this.clock = new THREE.Clock()
    this.cameraAngle = 0
    this.canvas = canvas
    this.autoShoot = autoShoot
    this.interval = interval
    this.stack = stack
    this.delay = delay
    this.eventBus = new Vue()
    this.init()
  }

  resize() {
    this.WIDTH = window.innerWidth
    this.HEIGHT = window.innerHeight
    this.renderer.setSize(this.WIDTH, this.HEIGHT)
    this.composer.setSize(this.WIDTH, this.HEIGHT)
    // this.camera.position.x = -100
    this.camera.position.z = this.HEIGHT / 1.5
    this.camera.aspect = this.WIDTH / this.HEIGHT
    this.camera.updateProjectionMatrix()
  }

  init() {
    // eslint-disable-next-line no-unused-vars
    this.eventBus.$on('destroyInstance', (instance) => {
      instance = null
    })

    window.addEventListener('resize', this.resize.bind(this))

    //* setup scene
    this.scene = new THREE.Scene()

    //* setup camera
    this.camera = new THREE.PerspectiveCamera(60, this.WIDTH / this.HEIGHT, 0.1, 5000)
    this.camera.position.y = 100
    this.camera.position.z = this.HEIGHT / 1.5
    this.camera.updateProjectionMatrix()

    //* setup renderer
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: false,
      alpha: true
    })
    this.renderer.setSize(this.WIDTH, this.HEIGHT)
    this.renderer.setClearColor(new THREE.Color(0, 0, 0), 0)

    // composer
    this.composer = new EffectComposer(this.renderer)
    this.composer.setSize(this.WIDTH, this.HEIGHT)
    this.composer.addPass(new RenderPass(this.scene, this.camera))
    this.composer.addPass(new AfterimagePass(0.93))
    this.composer.addPass(
      new UnrealBloomPass(new THREE.Vector2(this.WIDTH, this.HEIGHT), 6, 0.9, 0)
    )

    this.render()
  }

  shoot(stack = 1) {
    for (let i = 0; i < stack; i++) {
      const firework = new Firework({ artwork: this })
      this.fireworks.push(firework)
      firework.init()
    }
  }

  render() {

    if (this.delaySpendTime < Math.floor(this.delay * 60)) {
      this.delaySpendTime++
    } else {
      this._frame++
      this.autoShootTime++
      if (this.autoShoot && this.autoShootTime === Math.floor(this.interval * 60)) {
        this.shoot(this.stack)
        this.autoShootTime = 0
      }
      this.eventBus.$emit('onRender')
      if (this._frame % 2 === 0) {
        this.composer.render()
      }
    }
    if (this.animating) requestAnimationFrame(this.render.bind(this))
  }

  destroy() {
    this.renderer.renderLists.dispose()
    this.eventBus.$destroy()
    this.animating = false
  }
}
